import API from "../../core/api";
import { StripeFlags } from "../../resources/modelTypes";
import { loadStripe } from "@stripe/stripe-js";
import type { Stripe } from "@stripe/stripe-js";
import config from "config";
import React, { createContext, useCallback, useContext } from "react";

const FIVE_SECONDS = 5_000;
const THIRTY_SECONDS = 30_000;

// Module-level singleton — loadStripe must only be called once per page load.
const stripePromise: Promise<Stripe | null> = loadStripe(
  config.get("stripePublishableKey"),
  {
    betas: ["custom_checkout_manual_approval_1"],
  },
);

type CreateCheckoutSessionParams = {
  amountCents: number;
  giverName: string;
  giverEmail: string;
  note: string;
  coverFee: boolean;
};

type GuestStripeContextValue = {
  /** Shared Stripe.js instance for the guest checkout. */
  stripePromise: Promise<Stripe | null>;
  /** Stripe connected-account readiness for the host (webhook-driven). */
  stripeFlags: StripeFlags;
  /**
   * Create a Checkout Session for a guest gift on the given registry section.
   * Returns the client secret for CheckoutElementsProvider.
   */
  createCheckoutSession: (
    sectionId: number,
    params: CreateCheckoutSessionParams,
  ) => Promise<string>;
  /** Approve the manual payment attempt for a checkout session. */
  approveContribution: (sessionId: string) => Promise<void>;
  /**
   * Poll a section's contribution total until it moves off the given baseline
   * (the webhook landed) or the polling window elapses. `onSettled` fires once
   * with the final total. Returns a stop function to cancel the poll (e.g. on
   * unmount) without settling.
   */
  pollContributionStatus: (
    sectionId: number,
    baselineCents: number,
    onSettled: (totalCents: number) => void,
  ) => () => void;
};

const GuestStripeContext = createContext<GuestStripeContextValue | null>(null);

type GuestStripeProviderProps = {
  children: React.ReactNode;
  stripeFlags: StripeFlags;
};

/**
 * Owns the guest-facing Stripe payment flow on the public website: the shared
 * Stripe.js instance, checkout session creation, manual approval, and
 * post-payment contribution-status polling. The host readiness flags are
 * seeded from the server-rendered site payload (window.__DATA__).
 */
const GuestStripeProvider = ({
  children,
  stripeFlags,
}: GuestStripeProviderProps) => {
  const createCheckoutSession = useCallback(
    async (
      sectionId: number,
      params: CreateCheckoutSessionParams,
    ): Promise<string> => {
      const response = await API.post(
        "stripe/checkout-session/",
        {},
        {
          sectionId,
          amountCents: params.amountCents,
          giverName: params.giverName,
          giverEmail: params.giverEmail,
          note: params.note,
          coverFee: params.coverFee,
        },
      );
      return (response as { clientSecret: string }).clientSecret;
    },
    [],
  );

  const approveContribution = useCallback(
    async (sessionId: string): Promise<void> => {
      const result = (await API.post("stripe/approve/", {}, { sessionId })) as {
        type: string;
      };
      if (result.type !== "success") {
        throw new Error("Payment approval failed.");
      }
    },
    [],
  );

  const pollContributionStatus = useCallback(
    (
      sectionId: number,
      baselineCents: number,
      onSettled: (totalCents: number) => void,
    ): (() => void) => {
      let settled = false;
      let intervalId: ReturnType<typeof setInterval> | null = null;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;

      const clearTimers = () => {
        if (intervalId) clearInterval(intervalId);
        if (timeoutId) clearTimeout(timeoutId);
      };

      const finish = (totalCents: number) => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimers();
        onSettled(totalCents);
      };

      intervalId = setInterval(() => {
        API.get("stripe/contribution-status/", {}, { section_id: sectionId })
          .then((response: { contributionTotalCents: number }) => {
            if (response.contributionTotalCents !== baselineCents) {
              // Webhook landed — settle early rather than spinning the window out.
              finish(response.contributionTotalCents);
            }
          })
          .catch((error: unknown) => {
            console.error("Error refreshing contribution status:", error);
          });
      }, FIVE_SECONDS);

      timeoutId = setTimeout(() => finish(baselineCents), THIRTY_SECONDS);

      // Cancel without settling — used as effect cleanup on unmount.
      return () => {
        settled = true;
        clearTimers();
      };
    },
    [],
  );

  return (
    <GuestStripeContext.Provider
      value={{
        approveContribution,
        createCheckoutSession,
        pollContributionStatus,
        stripeFlags,
        stripePromise,
      }}
    >
      {children}
    </GuestStripeContext.Provider>
  );
};

const useGuestStripe = (): GuestStripeContextValue => {
  const context = useContext(GuestStripeContext);
  if (!context) {
    throw new Error("useGuestStripe must be used within a GuestStripeProvider");
  }
  return context;
};

export { GuestStripeProvider, useGuestStripe };
